/**
 * DataFreshness indicator — shows relative time since last API data refresh.
 * Format: "Updated: Xh ago", "15m ago", "just now"
 */

import { Clock } from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

export interface DataFreshnessProps {
  timestamp?: string;
  className?: string;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;

  if (Number.isNaN(diffMs) || diffMs < 0) return 'just now';

  const diffMinutes = Math.floor(diffMs / 60_000);
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function DataFreshness({ timestamp, className }: DataFreshnessProps) {
  if (!timestamp) return null;

  const relative = formatRelativeTime(timestamp);

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs text-secondary whitespace-nowrap ${className ?? ''}`}
    >
      <Clock className="w-3 h-3" />
      Updated: {relative}
    </span>
  );
}
